<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { IResource } from '@bc-library/interface'
import DefaultProfile from '@/assets/svg/profile.svg'
import { useUserStore } from '@/config/store/userStore'

const userStore = useUserStore()

type IResourceImageProps = {
  resource?: Pick<IResource, 'lastname' | 'firstname' | 'picture' | '_id'> & { role?: IResource['role'] }
  width?: string
  height?: string
  size?: string
  shadow?: string
  popover?: boolean
  rounded?: string
  to?: {
    name: string
    params?: any
    query?: any
  }
  disableLink?: boolean
  isCandidate?: boolean
}

const props = withDefaults(defineProps<IResourceImageProps>(), {
  width: 'w-7',
  height: 'h-7',
  shadow: 'shadow-sm',
  rounded: 'rounded-full',
  popover: true,
  disableLink: false,
  isCandidate: false,
})
const avatarSize = computed(() => {
  return props.size ? props.size : `${props.width} ${props.height}`
})

const onClick = (event) => {
  if (!props.disableLink)
    event.stopPropagation()
}

const letters = computed(() => {
  return `${props.resource.firstname?.charAt(0) || ''}${props.resource.lastname?.charAt(0) || ''}`
})

const imageError = ref(false)

watch(() => props.resource?.picture, () => {
  imageError.value = false
})

const shouldShowLink = computed(() => {
  return !props.disableLink && userStore.user?.profile?.modules?.hr?.resourceProfileAccess
})

const isCandidateProfile = computed(() => props.isCandidate || props.resource?.role === 'candidate')

const toProps = computed(() => {
  if(props.to) {
    return props.to
  }

  if(isCandidateProfile.value) {
    return {
      name: 'RecruitmentProfile',
      params: { resourceId: props.resource?._id },
    }
  }

  return {
    name: 'EmployeeProfile',
    params: { resourceId: props.resource?._id },
  }
})

</script>

<template>
  <component
    :is="shouldShowLink ? 'router-link' : 'div'"
    v-if="resource" class="bg-transparent shrink-0 relative" :class="[ avatarSize, shadow, rounded, popover ? 'cursor-help' : undefined]" :to="toProps"
    :title="!popover ? null : `${resource.firstname} ${resource.lastname}`"
    :aria-label="`Navigate to profile ${resource.firstname || ''} ${resource.lastname || ''}`"
    @click="onClick"
  >
    <img
      v-if="resource.picture && !imageError" width="15px" height="15px" loading="lazy" :class="rounded" :src="resource.picture" :alt="`Avatar de ${resource.firstname} ${resource.lastname}`"
      class="shadow-sm w-full h-full object-cover"
      @error="() => imageError = true"
    >
    <template v-else>
      <div :class="[rounded]" class="text-xs absolute inset-0 flex items-center justify-center dark:bg-primary-400/20 bg-primary-700/40 text-white">
        <p>{{ letters }}</p>
      </div>
    </template>
  </component>
</template>

<style scoped>
</style>
